import { useMemo, useState } from 'react';
import { AdminScreen, LoadOrError, useResource } from './Overview';
import { Button, Card, EmptyState, Icon, Input, Select, Spinner, StatusPill, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTimePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface AuditRow {
  id: string;
  actor_user_id: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  business_id: string | null;
  ip: string | null;
  metadata: string | null;
  created_at: string;
  actorEmail: string | null;
}

interface AuditPayload {
  logs: AuditRow[];
}

const PAGE_SIZES = [25, 50, 100];

function prettyMetadata(raw: string | null): string {
  if (!raw) return tr('sem metadados');
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function actorTone(role: string | null): 'neutral' | 'positive' | 'warning' {
  if (role === 'platform_admin') return 'positive';
  if (role === 'owner') return 'warning';
  return 'neutral';
}

export function AdminAuditPage(): JSX.Element {
  usePageMeta(tr('Auditoria — administração'), tr('Histórico de ações administrativas e sensíveis da plataforma.'), '/admin/auditoria');

  const toast = useToast();
  const [limit, setLimit] = useState(500);
  const resource = useResource<AuditPayload>(
    () => api.get<AuditPayload>(`/api/admin/audit?limit=${limit}`),
    [limit]
  );

  const [actionFilter, setActionFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const logs = resource.data?.logs ?? [];

  const visible = useMemo(() => {
    const action = actionFilter.trim().toLowerCase();
    const target = targetFilter.trim().toLowerCase();
    return logs.filter((log) => {
      if (action && !log.action.toLowerCase().includes(action)) return false;
      if (!target) return true;
      return [log.business_id, log.resource_id, log.actor_user_id, log.actorEmail]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(target));
    });
  }, [logs, actionFilter, targetFilter]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const slice = visible.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <AdminScreen
      title={tr("Auditoria")}
      description={tr("Toda ação administrativa e sensível do sistema: quem fez, o quê, em qual recurso, quando e com quais metadados. O registro é somente leitura — não há rota para apagar eventos.")}
    >
      <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className="nl-label" htmlFor="audit-action">{tr("Filtrar por ação")}</label>
          <Input
            id="audit-action"
            value={actionFilter}
            maxLength={80}
            placeholder={tr("ex.: subscription_status_changed")}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div>
          <label className="nl-label" htmlFor="audit-target">{tr("Filtrar por empresa, recurso ou responsável")}</label>
          <Input
            id="audit-target"
            value={targetFilter}
            maxLength={120}
            placeholder={tr("id ou e-mail")}
            onChange={(e) => {
              setTargetFilter(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div>
          <label className="nl-label" htmlFor="audit-limit">{tr("Registros a buscar no backend")}</label>
          <Select
            id="audit-limit"
            value={String(limit)}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
          >
            {[100, 200, 300, 500].map((option) => (
              <option key={option} value={option}>{tr("Últimos")} {option}</option>
            ))}
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="secondary" disabled={resource.loading} onClick={() => { void resource.reload(); toast(tr('Trilha de auditoria recarregada.')); }}>
            {resource.loading ? <Spinner label="" /> : <Icon name="search" />} {tr("Recarregar")}
          </Button>
        </div>
      </div>

      <LoadOrError loading={resource.loading} error={resource.error} />

      {!resource.loading && visible.length === 0 && !resource.error ? (
        <EmptyState
          title={tr("Nenhum evento de auditoria")}
          description={logs.length ? tr('Os filtros não casaram com nenhum dos registros carregados.') : tr('Ainda não há ações registradas nesta base.')}
        />
      ) : null}

      {slice.length > 0 ? (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[64rem] text-left text-sm">
              <caption className="sr-only">{tr("Eventos de auditoria")}</caption>
              <thead className="bg-surface text-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("Quando")}</th>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("Ação")}</th>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("Recurso")}</th>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("Quem executou")}</th>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("IP")}</th>
                  <th scope="col" className="px-4 py-3 font-semibold">{tr("Metadados")}</th>
                </tr>
              </thead>
              <tbody>
                {slice.map((log) => (
                  <tr key={log.id} className="border-t border-line align-top">
                    <td className="px-4 py-3 tabular-nums">{dateTimePt(log.created_at)}</td>
                    <td className="px-4 py-3">
                      <code className="break-all">{log.action}</code>
                    </td>
                    <td className="px-4 py-3">
                      <p>{log.resource_type ?? '—'}</p>
                      {log.resource_id ? <p className="nl-hint break-all">{tr("id")} {log.resource_id}</p> : null}
                      {log.business_id ? <p className="nl-hint break-all">{tr("empresa")} {log.business_id}</p> : null}
                    </td>
                    <td className="px-4 py-3">
                      <p className="break-all">{log.actorEmail ?? tr('usuário removido')}</p>
                      <div className="mt-1">
                        <StatusPill tone={actorTone(log.actor_role)}>{log.actor_role ?? tr('papel não registrado')}</StatusPill>
                      </div>
                    </td>
                    <td className="px-4 py-3 break-all">{log.ip ?? '—'}</td>
                    <td className="px-4 py-3">
                      <details>
                        <summary className="cursor-pointer text-sm font-semibold">{tr("Ver detalhes")}</summary>
                        <pre className="mt-2 max-w-md overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-surface p-2 text-xs">
                          {prettyMetadata(log.metadata)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="nl-hint">
              {visible.length} {tr("de")} {logs.length} {tr("registros carregados · página")} {safePage + 1} {tr("de")} {totalPages}.
            </p>
            <div className="flex items-center gap-2">
              <label className="nl-label mb-0" htmlFor="audit-page-size">{tr("Por página")}</label>
              <Select
                id="audit-page-size"
                className="w-24"
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </Select>
              <Button variant="secondary" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                <Icon name="back" size={16} /> {tr("Anterior")}
              </Button>
              <Button variant="secondary" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>
                {tr("Próxima")} <Icon name="chevron" size={16} />
              </Button>
            </div>
          </div>
          <p className="nl-hint mt-2">
            {tr("A rota")} <code>{tr("GET /api/admin/audit")}</code> {tr("aceita apenas")} <code>{tr("limit")}</code> {tr("(máximo 500) e ordena do mais recente para o mais antigo; não existe")} <code>{tr("offset")}</code> {tr("no backend, então a navegação acima percorre o conjunto já carregado.")}
          </p>
        </>
      ) : null}
    </AdminScreen>
  );
}
